import { create } from 'zustand';

/**
 * authStore.ts - Section 49.2: Zustand holds only "current session" UI
 * state (who's logged in, right now, in this tab) - NOT a cache of server
 * data. The actual user PROFILE (email, preferred language, etc.) is
 * fetched and cached by TanStack Query (see features/auth/hooks) and
 * would drift out of sync if we duplicated it here too.
 */
export interface CurrentUser {
  publicId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  accountType: string;
  status: string;
  preferredLanguage: string;
  /** Set on accounts provisioned with a credential someone else chose (e.g. the production
   *  bootstrap SUPER_ADMIN) - RequireAuth redirects to /change-password-required until this
   *  clears, since every other API call 403s with PASSWORD_CHANGE_REQUIRED anyway. */
  mustChangePassword: boolean;
  /** Fine-grained role names (SUPER_ADMIN, VERIFICATION_OFFICER, CORPORATE_ADMIN, PARTNER, DRIVER, CLIENT, ...) decoded from the JWT's `roles` claim - see `@/lib/jwt`. `accountType` alone can't distinguish these. */
  roles: string[];
  /** Union of every permission granted by any assigned role, decoded from the JWT's `permissions` claim - see `@/lib/jwt`. What `usePermissions()`/`hasPermission()` actually check. */
  permissions: string[];
}

interface AuthState {
  currentUser: CurrentUser | null;
  isAuthenticated: boolean;
  setCurrentUser: (user: CurrentUser | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  currentUser: null,
  isAuthenticated: false,
  setCurrentUser: (user) => set({ currentUser: user, isAuthenticated: user !== null }),
  clear: () => set({ currentUser: null, isAuthenticated: false }),
}));
